/**
 * Tenant administration API endpoints
 */

import { fetchApi } from '../core';
import type {
  AddTenantMemberData,
  EffectiveModelsResponse,
  TenantAiConfigResponse,
  TenantEntitlementsResponse,
  TenantMemberListResponse,
  TenantMemberResponse,
  TenantResponse,
  TenantThemeResponse,
  UpdateTenantMemberRolesData,
  UpdateTenantMemberStatusData,
  UpdateTenantProfileData,
  UpdateTenantSettingsData,
  UpdateTenantThemeData,
  UpsertTenantAiConfigData,
} from '../types';

export function getProfile(componentName: string = 'Unknown') {
  return fetchApi<TenantResponse>('/tenant/profile', {}, componentName);
}

/** Features enabled for the active tenant (computed from its plan server-side). */
export function getEntitlements(componentName: string = 'Unknown') {
  return fetchApi<TenantEntitlementsResponse>('/tenant/entitlements', {}, componentName);
}

/** The tenant's AI provider config (null when never configured). Admin-only. */
export function getAiConfig(componentName: string = 'Unknown') {
  return fetchApi<TenantAiConfigResponse | null>('/tenant/ai-config', {}, componentName);
}

/** Write the tenant's AI provider config (Enterprise / AiCustomProvider only). */
export function updateAiConfig(data: UpsertTenantAiConfigData, componentName: string = 'Unknown') {
  return fetchApi<TenantAiConfigResponse>('/tenant/ai-config', {
    method: 'PUT',
    body: JSON.stringify(data),
  }, componentName);
}

/** The tenant's effective model allowlist (catalog ∩ tenant allowlist). */
export function getAiModels(componentName: string = 'Unknown') {
  return fetchApi<EffectiveModelsResponse>('/tenant/ai-config/models', {}, componentName);
}

export function updateProfile(data: UpdateTenantProfileData, componentName: string = 'Unknown') {
  return fetchApi<TenantResponse>('/tenant/profile', {
    method: 'PUT',
    body: JSON.stringify(data),
  }, componentName);
}

export function getSettings(componentName: string = 'Unknown') {
  return fetchApi<Record<string, unknown>>('/tenant/settings', {}, componentName);
}

export function updateSettings(data: UpdateTenantSettingsData, componentName: string = 'Unknown') {
  return fetchApi<TenantResponse>('/tenant/settings', {
    method: 'PUT',
    body: JSON.stringify(data),
  }, componentName);
}

export function listMembers(
  params: { page?: number; limit?: number } = {},
  componentName: string = 'Unknown',
) {
  const query = new URLSearchParams();
  if (params.page) query.set('page', String(params.page));
  if (params.limit) query.set('limit', String(params.limit));
  const suffix = query.toString() ? `?${query}` : '';
  return fetchApi<TenantMemberListResponse>(`/tenant/members${suffix}`, {}, componentName);
}

/** One member by user id. Use this instead of scanning listMembers — the list
 * is paginated, so a scan would need every page to be sure. */
export function getMember(userId: string, componentName: string = 'Unknown') {
  return fetchApi<TenantMemberResponse>(`/tenant/members/${userId}`, {}, componentName);
}

/** Every member, by walking the paginated endpoint. For pickers that genuinely
 * need the whole roster; prefer a single page anywhere a page will do. */
export async function listAllMembers(componentName: string = 'Unknown') {
  const all: TenantMemberResponse[] = [];
  let page = 1;
  // Bounded so a bad total_pages cannot spin forever.
  for (let guard = 0; guard < 50; guard += 1) {
    const response = await listMembers({ page, limit: 100 }, componentName);
    all.push(...response.members);
    if (page >= response.total_pages || response.members.length === 0) break;
    page += 1;
  }
  return all;
}

export function addMember(data: AddTenantMemberData, componentName: string = 'Unknown') {
  return fetchApi<TenantMemberResponse>('/tenant/members', {
    method: 'POST',
    body: JSON.stringify(data),
  }, componentName);
}

export function updateMemberRoles(
  userId: string,
  data: UpdateTenantMemberRolesData,
  componentName: string = 'Unknown'
) {
  return fetchApi<TenantMemberResponse>(`/tenant/members/${userId}/roles`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }, componentName);
}

export function updateMemberStatus(
  userId: string,
  data: UpdateTenantMemberStatusData,
  componentName: string = 'Unknown'
) {
  return fetchApi<TenantMemberResponse>(`/tenant/members/${userId}/status`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }, componentName);
}

export interface AccessRequestResponse {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  message?: string;
  status: string;
  created_at: number;
}

export function listAccessRequests(componentName: string = 'Unknown') {
  return fetchApi<AccessRequestResponse[]>('/tenant/access-requests', {}, componentName);
}

export function approveAccessRequest(id: string, componentName: string = 'Unknown') {
  return fetchApi(`/tenant/access-requests/${encodeURIComponent(id)}/approve`, {
    method: 'POST',
  }, componentName);
}

export function rejectAccessRequest(id: string, componentName: string = 'Unknown') {
  return fetchApi(`/tenant/access-requests/${encodeURIComponent(id)}/reject`, {
    method: 'POST',
  }, componentName);
}

export function getTheme(componentName: string = 'Unknown') {
  return fetchApi<TenantThemeResponse>('/tenant/theme', {}, componentName);
}

export function updateTheme(data: UpdateTenantThemeData, componentName: string = 'Unknown') {
  return fetchApi<TenantThemeResponse>('/tenant/theme', {
    method: 'PUT',
    body: JSON.stringify(data),
  }, componentName);
}

export function setThemeLogo(attachmentId: string, componentName: string = 'Unknown') {
  return fetchApi<TenantThemeResponse>('/tenant/theme/logo', {
    method: 'PUT',
    body: JSON.stringify({ attachment_id: attachmentId }),
  }, componentName);
}

export function clearThemeLogo(componentName: string = 'Unknown') {
  return fetchApi<TenantThemeResponse>('/tenant/theme/logo', {
    method: 'DELETE',
  }, componentName);
}

export function setThemeFavicon(attachmentId: string, componentName: string = 'Unknown') {
  return fetchApi<TenantThemeResponse>('/tenant/theme/favicon', {
    method: 'PUT',
    body: JSON.stringify({ attachment_id: attachmentId }),
  }, componentName);
}

export function clearThemeFavicon(componentName: string = 'Unknown') {
  return fetchApi<TenantThemeResponse>('/tenant/theme/favicon', {
    method: 'DELETE',
  }, componentName);
}
